import { getCountries, getCountryCallingCode } from "libphonenumber-js";

import { DEFAULT_COUNTRY, DEFAULT_COUNTRY_CODE } from "../../constants";
import getSuffix from "../getSuffix";
import removeSuffix from "../removeSuffix";

/**
 * Create an object where the keys are country calling codes (country codes)
 * and the values are countries (e.g. "US").
 *
 * This object is used to try to determine the country a phone number
 * belongs to based on the first 3 digits of the number.
 */
const countryCodes = getCountries().reduce((acc, country) => {
  const countryCallingCode = getCountryCallingCode(country);
  acc[countryCallingCode] = country;

  return acc;
}, {});

/**
 * Phone numbers passed to this function are assumed to start with "+" and
 * begin with a valid country code.
 *
 * If the first digit of the phone number is "1," we assume the phone number
 * is US-based.
 *
 * If the first number is not "1," we try to find a two-digit country code
 * matching the first two digits of the phone number.
 *
 * If there is no matching two-digit country code, we try to find a 3-digit
 * country code. We stop after that, because all country codes are between
 * 1 and 3 digits.
 *
 * This is a naive way of parsing a country code from a potentially incomplete
 * phone number. It is designed for use in an environment where 99.6% of
 * phone numbers are US-based.
 */
const parseInternationalPhoneNumber = phoneNumberString => {
  const suffix = getSuffix(phoneNumberString);
  const unparsedPhoneNumber = removeSuffix(phoneNumberString);

  const isPossibleUSNumber = unparsedPhoneNumber[1] === DEFAULT_COUNTRY_CODE;

  if (isPossibleUSNumber) {
    const phoneNumber = unparsedPhoneNumber.slice(2);

    return {
      country: DEFAULT_COUNTRY,
      countryCode: DEFAULT_COUNTRY_CODE,
      phoneNumber,
      suffix
    };
  }

  const hasTwoDigitCountryCode =
    countryCodes[unparsedPhoneNumber.slice(1, 3)] ?? false;

  if (hasTwoDigitCountryCode) {
    const countryCode = unparsedPhoneNumber.slice(1, 3);
    const phoneNumber = unparsedPhoneNumber.slice(3);

    return {
      country: countryCodes[countryCode],
      countryCode,
      phoneNumber,
      suffix
    };
  }

  const hasThreeDigitCountryCode =
    countryCodes[unparsedPhoneNumber.slice(1, 4)] ?? false;

  if (hasThreeDigitCountryCode) {
    const countryCode = unparsedPhoneNumber.slice(1, 4);
    const phoneNumber = unparsedPhoneNumber.slice(4);

    return {
      country: countryCodes[countryCode],
      countryCode,
      phoneNumber,
      suffix
    };
  }

  return null;
};

/**
 * @param {string} phoneNumberString
 *
 * @typedef {object} parsePhoneNumberReturnValue
 * @property {string} country
 * @property {string} countryCallingCode
 * @property {string} suffix
 * @property {string} phoneNumber
 *
 * @returns {parsePhoneNumberReturnValue}
 */

const parsePhoneNumber = phoneNumberString => {
  const country = DEFAULT_COUNTRY;
  const countryCode = DEFAULT_COUNTRY_CODE;
  const phoneNumber = removeSuffix(phoneNumberString);
  const suffix = getSuffix(phoneNumberString);

  const parseAsInternational = phoneNumberString[0] === "+";

  const defaultReturnValue = {
    country,
    countryCode,
    phoneNumber,
    suffix
  };

  if (!parseAsInternational) {
    return defaultReturnValue;
  }

  const parsedInternationalPhoneNumber = parseInternationalPhoneNumber(
    phoneNumberString
  );

  return parsedInternationalPhoneNumber || defaultReturnValue;
};

export default parsePhoneNumber;
